import { supabase } from "../supabaseClient";
import { trackGoal } from "./datafast";
import { attributeCurrentAffiliateSignup } from "./affiliate";

const API_BASE = process.env.REACT_APP_API_URL || "";
const PENDING_KEY = "pending_checkout_intent";
const CHECKOUT_TRACKING_KEY = "datafast_checkout_intent";
const VALID_PLANS = ["pro", "elite"];
const PLAN_PRICING = {
  pro: { monthly: "29.99", annual: "270" },
  elite: { monthly: "49.99", annual: "450" },
};

const normalizePlan = (value) => String(value || "").trim().toLowerCase();
const normalizeInterval = (value) =>
  String(value || "").trim().toLowerCase() === "annual" ? "annual" : "monthly";
const resolvePlanPrice = (plan, interval) =>
  PLAN_PRICING?.[plan]?.[interval] || "";

const readCookie = (name) => {
  if (typeof document === "undefined") return "";
  const target = String(name || "").trim();
  if (!target) return "";
  const parts = (document.cookie || "").split(";");
  for (const part of parts) {
    const [rawKey, ...rest] = String(part || "").split("=");
    const key = rawKey?.trim();
    if (!key || key !== target) continue;
    const value = rest.join("=").trim();
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }
  return "";
};

const readDatafastAttribution = () => ({
  visitorId: readCookie("datafast_visitor_id"),
  sessionId: readCookie("datafast_session_id"),
});

const buildReturnUrl = (path, fallbackPath) => {
  if (typeof window === "undefined") return "";
  const origin = window.location.origin || "";
  const raw = path || fallbackPath;
  if (!raw) return origin;
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalized = raw.startsWith("/") ? raw : fallbackPath || "/dashboard";
  return `${origin}${normalized}`;
};

const appendQueryParam = (url, key, value) => {
  if (!url || !key) return url;
  try {
    const parsed = new URL(url);
    if (!parsed.searchParams.has(key)) {
      parsed.searchParams.set(key, value);
    }
    return parsed.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }
};

export const savePendingCheckout = (intent) => {
  if (typeof window === "undefined") return;
  const plan = normalizePlan(intent?.plan);
  if (!VALID_PLANS.includes(plan)) return;
  const payload = {
    plan,
    interval: normalizeInterval(intent?.interval),
    source: intent?.source || "",
  };
  window.localStorage.setItem(PENDING_KEY, JSON.stringify(payload));
};

export const readPendingCheckout = () => {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const plan = normalizePlan(parsed?.plan);
    if (!VALID_PLANS.includes(plan)) return null;
    return {
      plan,
      interval: normalizeInterval(parsed?.interval),
      source: parsed?.source || "",
    };
  } catch {
    return null;
  }
};

export const clearPendingCheckout = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(PENDING_KEY);
};

export const readCheckoutTracking = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_TRACKING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const plan = normalizePlan(parsed?.plan);
    if (!VALID_PLANS.includes(plan)) return null;
    return {
      plan,
      interval: normalizeInterval(parsed?.interval),
      price: resolvePlanPrice(plan, normalizeInterval(parsed?.interval)),
    };
  } catch {
    return null;
  }
};

export const clearCheckoutTracking = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(CHECKOUT_TRACKING_KEY);
};

const saveCheckoutTracking = (intent) => {
  if (typeof window === "undefined") return;
  const plan = normalizePlan(intent?.plan);
  if (!VALID_PLANS.includes(plan)) return;
  const payload = {
    plan,
    interval: normalizeInterval(intent?.interval),
    price: resolvePlanPrice(plan, normalizeInterval(intent?.interval)),
  };
  try {
    window.sessionStorage.setItem(CHECKOUT_TRACKING_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
};

export const startStripeCheckout = async ({
  plan,
  interval = "monthly",
  source = "",
  successPath = "/dashboard?checkout=success",
  cancelPath = "/pricing",
} = {}) => {
  const normalizedPlan = normalizePlan(plan);
  if (!VALID_PLANS.includes(normalizedPlan)) {
    throw new Error("Please choose a plan to continue.");
  }
  const normalizedInterval = normalizeInterval(interval);
  const price = resolvePlanPrice(normalizedPlan, normalizedInterval);

  try {
    const { data } = await supabase.auth.getSession();
    const accessToken = data?.session?.access_token;
    if (!accessToken) {
      throw new Error("Please sign in to continue.");
    }
    try {
      await attributeCurrentAffiliateSignup();
    } catch {
      // Attribution should not block checkout.
    }

    const payload = {
      plan: normalizedPlan,
      interval: normalizedInterval,
      source,
      successUrl: buildReturnUrl(successPath, "/dashboard?checkout=success"),
      cancelUrl: appendQueryParam(buildReturnUrl(cancelPath, "/pricing"), "checkout", "cancel"),
    };

    const datafast = readDatafastAttribution();
    if (datafast.visitorId) {
      payload.datafastVisitorId = datafast.visitorId;
    }
    if (datafast.sessionId) {
      payload.datafastSessionId = datafast.sessionId;
    }

    const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    const response = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(response?.error || "Unable to start checkout.");
    }
    if (!response?.url) {
      throw new Error("Stripe Checkout link unavailable.");
    }
    trackGoal("initiate_checkout", {
      plan_type: normalizedPlan,
      currency: "USD",
      ...(price ? { price } : {}),
    });
    saveCheckoutTracking({ plan: normalizedPlan, interval: normalizedInterval });
    window.location.href = response.url;
  } catch (err) {
    trackGoal("upgrade_failed", {
      plan_type: normalizedPlan,
      reason: err?.message || "checkout_error",
    });
    throw err;
  }
};

export const startStripePortal = async ({ returnPath = "/my-info" } = {}) => {
  const { data } = await supabase.auth.getSession();
  const accessToken = data?.session?.access_token;
  if (!accessToken) {
    throw new Error("Please sign in to continue.");
  }

  const payload = {
    returnUrl: buildReturnUrl(returnPath, "/my-info"),
  };

  const res = await fetch(`${API_BASE}/api/stripe/portal`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  const response = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(response?.error || "Unable to open billing portal.");
  }
  if (!response?.url) {
    throw new Error("Stripe portal link unavailable.");
  }
  window.location.href = response.url;
};
